import React, { useEffect, useState, useCallback } from 'react';
import './ContributePage.css';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import VpnKeyRoundedIcon from '@mui/icons-material/VpnKeyRounded';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import ShoppingBagRoundedIcon from '@mui/icons-material/ShoppingBagRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CircularProgress from '@mui/material/CircularProgress';

const API_BASE = process.env.REACT_APP_API_URL || 'https://api.spayis.com/api';

const STATUS_LABELS = {
  IN_PROGRESS: 'Actif',
  COMPLETED: 'Terminé',
  CANCELLED: 'Annulé',
  EXPIRED: 'Expiré',
  CLOSED: 'Clôturé',
};

const CLOSED_STATUSES = ['COMPLETED', 'CANCELLED', 'EXPIRED', 'CLOSED'];

function formatAmount(value, currency) {
  return `${Number(value || 0).toLocaleString('fr-FR')} ${currency || 'XAF'}`;
}

function daysRemaining(deadline, fallbackDays) {
  if (deadline) {
    const diff = new Date(deadline).getTime() - Date.now();
    return Math.max(Math.ceil(diff / 86400000), 0);
  }
  return fallbackDays ?? 0;
}

export default function ContributePage() {
  const [code] = useState(() => new URLSearchParams(window.location.search).get('code'));
  const [group, setGroup] = useState(null);
  const [loadState, setLoadState] = useState('loading'); // loading | ready | error
  const [loadError, setLoadError] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const fetchGroup = useCallback(async () => {
    if (!code) {
      setLoadState('error');
      setLoadError('Aucun code de groupe fourni dans le lien.');
      return;
    }
    setLoadState('loading');
    try {
      const res = await fetch(`${API_BASE}/groups/by-code/${encodeURIComponent(code)}`);
      if (!res.ok) {
        throw new Error(res.status === 404 ? "Ce code d'invitation est invalide." : 'Impossible de charger le groupe.');
      }
      const data = await res.json();
      setGroup(data);
      setLoadState('ready');
    } catch (err) {
      setLoadError(err.message || 'Une erreur est survenue.');
      setLoadState('error');
    }
  }, [code]);

  useEffect(() => {
    fetchGroup();
  }, [fetchGroup]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: group?.name, text: `Contribue à "${group?.name}" avec le code ${group?.invitationCode}`, url });
      } catch {
        // partage annulé par l'utilisateur
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch {
      // clipboard indisponible
    }
  };

  if (loadState === 'loading') {
    return (
      <div className="cp-root cp-center">
        <CircularProgress sx={{ color: '#065F46' }} />
        <p className="cp-muted" style={{ marginTop: 16 }}>Chargement du groupe...</p>
      </div>
    );
  }

  if (loadState === 'error' || !group) {
    return (
      <div className="cp-root cp-center">
        <ErrorOutlineRoundedIcon className="cp-error-icon" />
        <h2>Oups</h2>
        <p className="cp-muted">{loadError}</p>
      </div>
    );
  }

  const progress = group.targetAmount ? Math.min((group.currentAmount / group.targetAmount) * 100, 100) : 0;
  const remaining = Math.max(group.targetAmount - group.currentAmount, 0);
  const days = daysRemaining(group.deadline, group.durationInDays);
  const statusKey = String(group.status || '').toUpperCase();
  const statusLabel = STATUS_LABELS[statusKey] || group.status;
  const isActive = !CLOSED_STATUSES.includes(statusKey);

  return (
    <div className="cp-root">
      <header className="cp-header">
        <span className="cp-header-title">{group.name}</span>
        <button className="cp-code-badge" onClick={handleShare}>
          {group.invitationCode}
          <IosShareRoundedIcon fontSize="inherit" />
        </button>
      </header>

      {shareCopied && <div className="cp-toast">Lien copié !</div>}

      <main className="cp-main">
        <section className="cp-card cp-overview">
          <div className="cp-icon-circle">
            {group.categoryId?.icon ? (
              <span className="cp-category-emoji">{group.categoryId.icon}</span>
            ) : (
              <FavoriteRoundedIcon style={{ color: '#065F46' }} />
            )}
          </div>

          <div className="cp-stats-row">
            <div className="cp-stat">
              <span className="cp-stat-label">Épargné</span>
              <span className="cp-stat-value cp-accent">{formatAmount(group.currentAmount, group.currency)}</span>
            </div>
            <div className="cp-stat">
              <span className="cp-stat-label">Objectif</span>
              <span className="cp-stat-value">{formatAmount(group.targetAmount, group.currency)}</span>
            </div>
            <div className="cp-stat">
              <span className="cp-stat-label">Restant</span>
              <span className="cp-stat-value cp-secondary-dark">{formatAmount(remaining, group.currency)}</span>
            </div>
          </div>

          <div className="cp-progress-track">
            <div className="cp-progress-fill" style={{ width: `${progress}%` }} />
          </div>

          <div className="cp-badges-row">
            <span className="cp-pill">
              <AccessTimeRoundedIcon fontSize="inherit" /> {days} jour{days > 1 ? 's' : ''} restant{days > 1 ? 's' : ''}
            </span>
            <span className="cp-pill">
              <GroupsRoundedIcon fontSize="inherit" /> {group.members?.length ?? 0}/{group.maxMembers} membres
            </span>
            <span className={`cp-pill cp-status ${isActive ? 'cp-status-active' : ''}`}>{statusLabel}</span>
          </div>
        </section>

        <section className="cp-card cp-info">
          {group.description && (
            <div className="cp-info-row">
              <InfoOutlinedIcon fontSize="small" />
              <span>{group.description}</span>
            </div>
          )}
          {group.categoryId?.name && (
            <div className="cp-info-row">
              <ShoppingBagRoundedIcon fontSize="small" />
              <span>{group.categoryId.name}</span>
            </div>
          )}
          {group.deadline && (
            <div className="cp-info-row">
              <CalendarMonthRoundedIcon fontSize="small" />
              <span>
                Échéance : {new Date(group.deadline).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
            </div>
          )}
          <div className="cp-info-row cp-info-row-code">
            <VpnKeyRoundedIcon fontSize="small" />
            <span>
              Code d'invitation : <strong>{group.invitationCode}</strong>
            </span>
            <button className="cp-share-btn" onClick={handleShare}>
              <IosShareRoundedIcon fontSize="inherit" /> Partager
            </button>
          </div>
        </section>

        {group.articles?.length > 0 && (
          <section className="cp-card cp-articles">
            <h3 className="cp-section-title">
              <ShoppingBagRoundedIcon fontSize="small" /> Articles
            </h3>
            {group.articles.map((article, idx) => (
              <div className="cp-article-row" key={article._id || idx}>
                <span className="cp-article-name">{article.name}</span>
                <span className="cp-article-detail">
                  {formatAmount(article.price, group.currency)} × {article.quantity}
                </span>
              </div>
            ))}
          </section>
        )}
      </main>

      <div className="cp-cta-bar">
        <button className="cp-cta-btn" onClick={() => setSheetOpen(true)} disabled={!isActive}>
          <AddCircleRoundedIcon fontSize="small" /> {isActive ? 'Contribuer' : statusLabel}
        </button>
      </div>

      {sheetOpen && (
        <ContributeSheet
          group={group}
          onClose={() => setSheetOpen(false)}
          onSuccess={(newAmount) => {
            setGroup((g) => ({ ...g, currentAmount: g.currentAmount + newAmount }));
          }}
        />
      )}
    </div>
  );
}

function ContributeSheet({ group, onClose, onSuccess }) {
  const [anonymous, setAnonymous] = useState(true);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [step, setStep] = useState('form'); // form | submitting | success
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const numericAmount = Number(amount);
    if (!numericAmount || numericAmount <= 0) {
      setError('Merci de saisir un montant valide.');
      return;
    }
    if (!phone.trim()) {
      setError('Le numéro de téléphone est requis pour finaliser le paiement.');
      return;
    }
    setError('');
    setStep('submitting');
    try {
      const body = { amount: numericAmount, contributorPhone: phone.trim() };
      if (!anonymous && name.trim()) body.contributorName = name.trim();
      if (note.trim()) body.note = note.trim();

      const res = await fetch(`${API_BASE}/groups/${group._id}/contribute/anonymous`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const errBody = await res.json().catch(() => null);
        throw new Error(errBody?.message || 'La contribution a échoué. Réessayez.');
      }
      const data = await res.json();
      setResult(data);
      setStep('success');
      onSuccess(numericAmount);
    } catch (err) {
      setError(err.message || 'Une erreur est survenue.');
      setStep('form');
    }
  };

  const copyUssd = async () => {
    if (!result?.paymentUrl) return;
    try {
      await navigator.clipboard.writeText(result.paymentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard indisponible
    }
  };

  return (
    <div className="cp-sheet-overlay" onClick={onClose}>
      <div className="cp-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="cp-sheet-handle" />
        <button className="cp-sheet-close" onClick={onClose} aria-label="Fermer">
          <CloseRoundedIcon fontSize="small" />
        </button>

        {step === 'success' ? (
          <div className="cp-success">
            <CheckCircleRoundedIcon className="cp-success-icon" />
            <h3>Contribution enregistrée</h3>
            <p className="cp-muted">Composez le code ci-dessous depuis votre téléphone pour finaliser le paiement.</p>
            <div className="cp-ussd-box" onClick={copyUssd}>
              <span>{result?.paymentUrl}</span>
              <ContentCopyRoundedIcon fontSize="small" />
            </div>
            {copied && <span className="cp-copied-label">Copié !</span>}
            <a className="cp-dial-link" href={`tel:${encodeURIComponent(result?.paymentUrl || '')}`}>
              Composer maintenant
            </a>
            <button className="cp-cta-btn cp-full" onClick={onClose}>
              Fermer
            </button>
          </div>
        ) : (
          <form className="cp-form" onSubmit={handleSubmit}>
            <h3 className="cp-sheet-title">Contribuer à « {group.name} »</h3>

            <div className="cp-toggle-row">
              <button type="button" className={`cp-toggle-btn ${anonymous ? 'active' : ''}`} onClick={() => setAnonymous(true)}>
                Anonyme
              </button>
              <button type="button" className={`cp-toggle-btn ${!anonymous ? 'active' : ''}`} onClick={() => setAnonymous(false)}>
                Mes informations
              </button>
            </div>

            {!anonymous && (
              <>
                <label className="cp-label" htmlFor="cp-name">Nom</label>
                <input
                  id="cp-name"
                  className="cp-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jean Dupont"
                  maxLength={100}
                />
              </>
            )}

            <label className="cp-label" htmlFor="cp-phone">Numéro de téléphone pour le paiement</label>
            <input
              id="cp-phone"
              className="cp-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="699 123 456"
              maxLength={20}
              inputMode="tel"
            />
            {anonymous && (
              <p className="cp-field-hint">Ce numéro sert uniquement à finaliser le paiement, votre identité reste anonyme.</p>
            )}

            <label className="cp-label" htmlFor="cp-amount">Montant ({group.currency})</label>
            <input
              id="cp-amount"
              className="cp-input"
              type="number"
              min="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="5000"
              inputMode="numeric"
            />

            <label className="cp-label" htmlFor="cp-note">Message (optionnel)</label>
            <textarea
              id="cp-note"
              className="cp-input cp-textarea"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={300}
              placeholder="Avec tout mon soutien !"
            />

            {error && <div className="cp-form-error">{error}</div>}

            <button type="submit" className="cp-cta-btn cp-full" disabled={step === 'submitting'}>
              {step === 'submitting' ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : 'Valider ma contribution'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
